/**
 * unitesDeForme — le SEUL endroit qui convertit entre les unités du fichier
 * Word et les unités d'avocat des outils de forme (spec #1858, règle commune
 * n°3 ; #1866).
 *
 * | réglage | unité d'avocat | unité du fichier |
 * |---|---|---|
 * | retraits | centimètre | twip, 1/1440 de pouce (`w:ind`) |
 * | espacement avant / après | point | vingtième de point (`w:spacing w:before`, `w:after`) |
 * | interligne | multiple de l'interligne simple | 240ᵉ de ligne (`w:spacing w:line`, `w:lineRule="auto"`) |
 * | épaisseur de bordure | point | huitième de point (`w:pBdr`, attribut `w:sz` de chaque côté) |
 * | marges de page | centimètre | twip (`w:pgMar`) |
 * | dimensions de page | nom de format (millimètres, ISO 216) | twip (`w:pgSz w:w`, `w:h`) |
 *
 * Une troisième unité pour la page seulement : l'ÉDITEUR prend et publie les
 * marges et les dimensions d'une section en pouces décimaux (`sections.list`,
 * `setPageMargins`, `setPageSetup` — mesuré le 2026-10-06,
 * `mesure-socle-page-et-defauts` : A4, 8,268 × 11,693 ; marge de 2 cm, 0,7875).
 * `twipsEnPouces` et `poucesEnTwips` ne sont pas arrondis : c'est l'appelant qui
 * compare au twip près, ou arrondit ce qu'il écrit.
 *
 * Source de l'interligne : ISO/IEC 29500-1 §17.3.1.33, exemple « 276 divided by
 * 240 » = 1,15 fois l'interligne simple,
 * https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.spacingbetweenlines
 * (vérifié 2026-10-02). L'éditeur écrit et lit ces mêmes unités
 * (`mesure-format-paragraphs`, 2026-10-02 : `setIndentation({left:567})` →
 * `w:ind w:left="567"` ; `setSpacing({before:120})` → `w:before="120"` ;
 * `setSpacing({line:360, lineRule:'auto'})` → `w:line="360"`).
 *
 * Source de l'épaisseur de bordure : ECMA-376, élément `left` (Left Paragraph
 * Border), attribut `sz` — « the width of this border is specified in
 * measurements of eighths of a point » (référence complète sur
 * `CANVAS_BORDER_WIDTH_PT_MIN`, `@hector/shared-types`). L'éditeur écrit cette
 * unité telle quelle, et n'accepte qu'un entier
 * (`mesure-format-paragraphs-bordure-reset`, C1 et C5, 2026-10-02 :
 * `setBorder({size:18})` → `w:sz="18"`, peint 3 px soit 2,25 pt ; `size:4.5`
 * refusé, `INVALID_INPUT`).
 *
 * Le lecteur de forme rend ses valeurs au centième. Module pur.
 */

import type { CanvasParagraphFormatKey } from '@hector/shared-types';

const TWIPS_PAR_POUCE = 1440;
const VINGTIEMES_PAR_POINT = 20;
const INTERLIGNE_SIMPLE = 240;
const HUITIEMES_PAR_POINT = 8;

const auCentieme = (valeur: number): number => Math.round(valeur * 100) / 100;

/**
 * Twips → centimètres, au centième. Le produit entier d'abord : 360 twips font
 * exactement 63,5 centièmes de centimètre, et un passage par `2.54` flottant
 * l'arrondirait tantôt à 0,63, tantôt à 0,64.
 */
export function twipsEnCm(twips: number): number {
  return Math.round((twips * 254) / TWIPS_PAR_POUCE) / 100;
}

/** Centimètres → twips entiers. */
export function cmEnTwips(cm: number): number {
  return Math.round((cm * TWIPS_PAR_POUCE) / 2.54);
}

/** Twips → pouces décimaux, l'unité de page de l'éditeur. Sans arrondi. */
export function twipsEnPouces(twips: number): number {
  return twips / TWIPS_PAR_POUCE;
}

/** Pouces décimaux de l'éditeur → twips, sans arrondi : une valeur publiée par l'éditeur n'est pas un entier de twips. */
export function poucesEnTwips(pouces: number): number {
  return pouces * TWIPS_PAR_POUCE;
}

/** Millimètres → twips entiers : les dimensions d'un format de page (ISO 216). */
export function mmEnTwips(mm: number): number {
  return Math.round((mm * TWIPS_PAR_POUCE) / 25.4);
}

/** Vingtièmes de point → points, au centième. */
export function vingtiemesEnPt(vingtiemes: number): number {
  return Math.round(vingtiemes * 5) / 100;
}

/** Points → vingtièmes de point entiers. */
export function ptEnVingtiemes(pt: number): number {
  return Math.round(pt * VINGTIEMES_PAR_POINT);
}

/** `w:line` sous la règle `auto` → multiple de l'interligne simple, au centième. */
export function interligneEnMultiple(ligne: number): number {
  return auCentieme(ligne / INTERLIGNE_SIMPLE);
}

/** Multiple de l'interligne simple → `w:line` entier, à écrire avec la règle `auto`. */
export function multipleEnInterligne(multiple: number): number {
  return Math.round(multiple * INTERLIGNE_SIMPLE);
}

/** `w:sz` d'un côté de bordure → points, au centième. */
export function huitiemesEnPt(huitiemes: number): number {
  return auCentieme(huitiemes / HUITIEMES_PAR_POINT);
}

/** Points → `w:sz` entier d'un côté de bordure. */
export function ptEnHuitiemes(pt: number): number {
  return Math.round(pt * HUITIEMES_PAR_POINT);
}

/**
 * L'écart toléré entre une valeur demandée et la valeur relue, par réglage
 * numérique : la somme des deux arrondis du trajet, et rien d'autre. La demande
 * est arrondie à l'unité entière du fichier (demi-pas au plus), puis le lecteur
 * arrondit au centième (0,005 au plus).
 *
 * | unité | demi-pas du fichier | + arrondi du lecteur | tolérance |
 * |---|---|---|---|
 * | cm | ½ twip = 0,00088 cm | 0,005 | 0,006 |
 * | pt | ½ vingtième = 0,025 pt | 0,005 | 0,03 |
 * | multiple | ½ × 1/240 = 0,0021 | 0,005 | 0,0075 |
 * | pt de bordure | ½ huitième = 0,0625 pt | 0,005 | 0,07 |
 *
 * Chacune reste sous le plus petit pas qu'un avocat demande (un centième de
 * centimètre, un vingtième de point, un centième de ligne ; pour une bordure,
 * le quart de point, plus petite épaisseur que la norme admet) : elle n'absorbe
 * aucun écart de forme.
 */
export const TOLERANCE_DE_REGLAGE: Readonly<Record<Exclude<CanvasParagraphFormatKey, 'alignment' | 'border'>, number>> = {
  indentLeftCm: 0.006,
  indentRightCm: 0.006,
  indentFirstLineCm: 0.006,
  spaceBeforePt: 0.03,
  spaceAfterPt: 0.03,
  lineSpacing: 0.0075,
  borderWidthPt: 0.07,
};
